/**
 * The nineteen figures the book captions good or bad itself.
 *
 * Eleven have a sound file the parser can read, so the rules can be asked whether they agree
 * with the man who wrote them. The first count is the one that matters: a fault on a figure the
 * book prints as a model is a cost whatever the caption is about, while a fault on one it warns
 * against may have landed for a reason the caption never raised.
 */

import type { FigureRow } from "@app/shared-types";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import { FaultStrip, Pill } from "../components/marks";
import Board from "../components/Board";
import { Failed, Kicker, Loading, Note } from "../components/Page";

/**
 * One square per chord, carrying the initial of the rule that faulted it.
 *
 * The three lists hold positions into `chords`, so a square is looked up rather than parsed.
 * Spacing is named first because it is the rule that speaks most often; a chord faulted twice
 * still gets one square, and the strip is a map of where to look, not a tally.
 */
function marksFor(figure: FigureRow): string[] {
  const spacing = new Set(figure.spacing);
  const doubling = new Set(figure.doubling);
  const progression = new Set(figure.progression);
  const marks: string[] = [];
  for (let index = 0; index < figure.chords.length; index += 1) {
    if (spacing.has(index)) marks.push("s");
    else if (doubling.has(index)) marks.push("d");
    else if (progression.has(index)) marks.push("p");
    else marks.push("");
  }
  return marks;
}

export default function FiguresPage(): JSX.Element {
  const { data, error, loading } = useAsync("figures", () => api.figures());

  const shell = (children: JSX.Element): JSX.Element => (
    <Board
      kicker="The book's own captions"
      title="Labelled figures"
      back={{ to: "/evidence", label: "Evidence" }}
      measure="wide"
    >
      {children}
    </Board>
  );
  if (loading) return shell(<Loading what="the labelled figures" />);
  if (error !== null) return shell(<Failed error={error} />);
  if (data === null) return shell(<Failed error="the figures came back empty" />);

  const readable = data.figures.filter((one) => one.chords.length > 0);
  const unread = data.figures.filter((one) => one.chords.length === 0);

  return (
    <Board
      kicker="The book's own captions"
      title="Labelled figures"
      back={{ to: "/evidence", label: "Evidence" }}
      lede={`${data.labelled ?? data.figures.length} figures carry a verdict in their caption. ${readable.length} have a sound file the parser can read, so the rules can be asked whether they agree with the man who wrote them.`}
      measure="wide"
    >
      <div className="space-y-7">
        {data.note ? <Note>{data.note}</Note> : null}

        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-rule bg-leaf p-4">
            <div className="font-mono text-[26px] leading-none">
              {data.goodFaulted}
              <span className="text-xs text-ink-quiet"> / {data.goodTotal}</span>
            </div>
            <p className="mt-1.5 text-[11px] leading-snug text-ink-faded">
              figures printed as <strong>models</strong> that a rule faulted
            </p>
          </div>
          <div className="rounded-xl border border-rule bg-leaf p-4">
            <div className="font-mono text-[26px] leading-none">
              {data.badFaulted}
              <span className="text-xs text-ink-quiet"> / {data.badTotal}</span>
            </div>
            <p className="mt-1.5 text-[11px] leading-snug text-ink-faded">
              figures the book <strong>warns against</strong> that a rule faulted
            </p>
          </div>
        </div>

        <ul className="grid gap-2 lg:grid-cols-2">
          {readable.map((one) => (
            <li key={one.name} className="rounded-xl border border-rule bg-leaf p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="font-mono text-[11px] text-ink-quiet">{one.name}</div>
                  <p className="mt-1 font-display text-[15px] italic leading-snug">{one.caption}</p>
                </div>
                <Pill tone={one.approved ? "clean" : "fault"}>
                  {one.approved ? "model" : "avoid"}
                </Pill>
              </div>
              <div className="mt-3">
                <FaultStrip marks={marksFor(one)} />
              </div>
              <p className="mt-2 text-[11px] text-ink-quiet">
                {one.faulted.length === 0
                  ? "no rule spoke against it"
                  : `${one.faulted.length} fault${one.faulted.length === 1 ? "" : "s"} · ${one.agrees ? "the rules agree with the caption" : "the rules disagree with the caption"}`}
              </p>
            </li>
          ))}
        </ul>

        <section className="space-y-2">
          <Kicker>Legend</Kicker>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-faded">
            <span>s spacing</span>
            <span>d doubling</span>
            <span>p progression</span>
            <span>blank clean</span>
          </div>
        </section>

        {unread.length > 0 ? (
          <section className="rounded-xl border border-departure bg-departure/40 p-4">
            <Kicker>{unread.length} more are MP3 only</Kicker>
            <p className="mt-2 text-sm leading-relaxed text-departure-deep">
              Transcribing them by eye would make this repository the ground truth instead of the
              book, so they stay unread — counted as unread, never as passed.
            </p>
            <ul className="mt-2 space-y-1 font-mono text-[11px] text-departure-ink">
              {unread.map((one) => (
                <li key={one.name}>{one.name}</li>
              ))}
            </ul>
          </section>
        ) : null}

        <p className="max-w-2xl text-xs leading-relaxed text-ink-faded">
          Read the first count, not the second. A fault on a figure the book prints as a model is a
          cost whatever the caption is about; a fault on one it warns against may have landed for a
          reason the caption never raised.
        </p>
      </div>
    </Board>
  );
}
